import React, { useState, useMemo, useEffect, useRef } from "react";
import { Button, FormControl, InputGroup, Pagination } from "react-bootstrap";
import { FaSearch, FaEdit, FaTrash, FaEye, FaPlus } from "react-icons/fa";
import "../assets/styles/ResponsiveTable.css";
import { useIsDraggable, getEnvironmentClass } from "../utils/environmentDetector";

//Enhanced Responsive Table Component

const ResponsiveTable = ({ data, columns, actions = [] }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [theme, setTheme] = useState("light");
  const [isMobileView, setIsMobileView] = useState(false);
  const containerRef = useRef(null);
  const searchBarRef = useRef(null);
  const [searchBarClass, setSearchBarClass] = useState('table-search-bar');

  // Use our custom hook to detect draggable environment
  const isInDraggable = useIsDraggable(containerRef);

  // Check for theme changes and mobile view
  useEffect(() => {
    // Theme detection
    const checkTheme = () => {
      const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
      setTheme(currentTheme);
    };
    
    // Initial theme check
    checkTheme();
    
    // Create observer to watch for theme changes
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'data-theme') {
          checkTheme();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });

    // Check container width to determine view mode
    const checkContainerWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        setIsMobileView(containerWidth < 700);
      }
    };

    // Initial width check
    checkContainerWidth();

    // Setup resize observer to detect container size changes
    const resizeObserver = new ResizeObserver(entries => {
      checkContainerWidth();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (searchBarRef.current) {
      setSearchBarClass(getEnvironmentClass(searchBarRef.current, 'table-search-bar'));
    }
  }, []);

  // Helper to get button class based on action type
  const getActionButtonClass = (action) => {
    if (!action.variant) return "";
    
    if (action.variant.includes("danger") || action.variant.includes("delete")) {
      return "btn-delete";
    }
    
    if (action.variant.includes("success") || action.variant.includes("edit")) {
      return "btn-edit";
    }
    
    if (action.variant.includes("info") || action.variant.includes("view")) {
      return "btn-view";
    }
    
    return "";
  };

  // Helper to get appropriate icon if none provided
  const getActionIcon = (action) => {
    if (action.icon) return action.icon;
    
    if (action.variant && action.variant.includes("danger")) {
      return <FaTrash />;
    }
    
    if (action.variant && action.variant.includes("success")) {
      return <FaEdit />;
    }
    
    if (action.variant && action.variant.includes("info")) {
      return <FaEye />;
    }
    
    if (action.label && action.label.toLowerCase().includes("add")) {
      return <FaPlus />;
    }
    
    return null;
  };

  // Get container class with environment detection
  const getContainerClass = () => {
    let classes = `responsive-table-container theme-${theme}`;
    if (isInDraggable) {
      classes += ' in-draggable-section';
    }
    if (isMobileView) {
      classes += ' force-mobile-view';
    }
    return classes;
  };

  // Get search bar container class with environment detection
  const getSearchBarContainerClass = () => {
    return getEnvironmentClass(searchBarRef.current, 'mb-3 table-action-buttons');
  };

  // Filtered Data Based on Search Term
  const filteredData = useMemo(() => {
    return data.filter((item) =>
      columns.some((col) =>
        item[col.key]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, columns, searchTerm]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handlePageChange = (page) => setCurrentPage(page);

  // Force text color based on theme and environment
  const textStyle = {
    color: theme === 'dark' ? '#ffffff' : '#333333'
  };

  // Render action buttons
  const renderActionButtons = (item) => (
    <div className="d-flex">
      {actions.map((action, i) => (
        <Button
          key={i}
          variant={action.variant || "outline-secondary"}
          size="sm"
          className={`table-action ${getActionButtonClass(action)}`}
          onClick={() => action.onClick(item)}
          title={action.label}
        >
          {getActionIcon(action)}
          <span className="action-tooltip">{action.label}</span>
        </Button>
      ))}
    </div>
  );

  return (
    <>
      {/* Search Bar */}
      <div ref={searchBarRef} className={searchBarClass}>
        <InputGroup>
          <FormControl
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            style={textStyle}
          />
          <InputGroup.Text style={textStyle}>
            <FaSearch />
          </InputGroup.Text>
        </InputGroup>
      </div>

      <div ref={containerRef} className={getContainerClass()}>
        {/* Desktop & Tablet View - Bootstrap Table */}
        <div className="table-container">
          <table className="responsive-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th 
                    key={col.key} 
                    className={col.hideOnMobile ? "hide-mobile" : ""} 
                    style={{
                      ...textStyle,
                      width: col.width,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {col.label}
                  </th>
                ))}
                {actions.length > 0 && <th style={{...textStyle, width: '15%', whiteSpace: 'nowrap'}}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {displayedData.map((item, index) => (
                <tr key={index}>
                  {columns.map((col) => (
                    <td 
                      key={col.key} 
                      className={col.hideOnMobile ? "hide-mobile" : ""} 
                      style={{
                        ...textStyle,
                        width: col.width
                      }}
                    >
                      {col.render ? col.render(item[col.key], item) : item[col.key]}
                    </td>
                  ))}
                  {actions.length > 0 && (
                    <td style={{width: '15%'}}>
                      {renderActionButtons(item)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile View - Cards */}
        <div className="mobile-table">
          {displayedData.map((item, index) => (
            <div key={index} className="card">
              <div className="card-header" style={textStyle}>
                <div className="card-header-content">
                  {columns[0] && (col => col.render ? col.render(item[columns[0].key], item) : item[columns[0].key])(columns[0])}
                </div>
                {actions.length > 0 && (
                  <div className="card-header-actions">
                    {renderActionButtons(item)}
                  </div>
                )}
              </div>
              <div className="card-content">
                {columns.slice(1).map((col) =>
                  col.hideOnMobile ? null : (
                    <div key={col.key} className="card-row">
                      <div className="card-label" style={textStyle}>{col.label}:</div>
                      <div className="card-value" style={textStyle}>{col.render ? col.render(item[col.key], item) : item[col.key]}</div>
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination className="justify-content-center mt-3">
            {[...Array(totalPages).keys()].map((page) => (
              <Pagination.Item
                key={page + 1}
                active={page + 1 === currentPage}
                onClick={() => handlePageChange(page + 1)}
              >
                {page + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        )}
      </div>
    </>
  );
};

export default ResponsiveTable;
